import { Injectable, NgZone, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { getApp } from 'firebase/app';
import {
	Database,
	DatabaseReference,
	child,
	get,
	getDatabase,
	goOffline,
	goOnline,
	onDisconnect,
	onValue,
	push,
	ref,
	remove,
	serverTimestamp,
	set,
	update,
} from 'firebase/database';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { ConfigService } from '../config/config.service';
import { User } from '../../models/user';
import { PresenceDevice } from './presence-status';

/** Heartbeat proves the page is alive. Runs whether visible or not; iOS simply stops it when frozen. */
const HEARTBEAT_INTERVAL_MS = 60_000;
/** At most one lastActive write per window while the user keeps interacting. */
const ACTIVE_WRITE_THROTTLE_MS = 30_000;

const ACTIVITY_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'scroll', 'wheel'];
/** Media events don't bubble, but a capturing listener on document still sees them. */
const MEDIA_EVENTS = ['play', 'timeupdate'];

interface PresenceContext {
	page: string;
	categoryId: string | null;
	courseId: string | null;
}

/**
 * Publishes this tab's presence to Realtime Database at presence/{projectId}/{uid}/{connectionId}.
 *
 * Built around iOS Safari, where most learners are: a backgrounded page is frozen without notice,
 * so nothing here relies on running at exit. `onDisconnect` removes the node when the socket
 * drops, and the dashboard treats a stale heartbeat as offline for the cases where it doesn't.
 * All status logic lives in presence-status.ts and runs on read.
 */
@Injectable({
	providedIn: 'root'
})
export class PresenceService {

	private readonly authService = inject(AuthService);
	private readonly configService = inject(ConfigService);
	private readonly router = inject(Router);
	private readonly zone = inject(NgZone);

	private db: Database | null = null;
	private connRef: DatabaseReference | null = null;
	private trackedUid: string | null = null;
	private teardown: (() => void)[] = [];

	private since: number | null = null;
	private serverTimeOffset = 0;
	private lastInteractionAt = 0;
	private lastActiveWriteAt = 0;
	private context: PresenceContext = { page: '', categoryId: null, courseId: null };
	private readonly device: PresenceDevice = detectDevice();

	/** Called once at app start; follows login and logout from then on. */
	init(): void {
		if (!environment.firebase?.databaseURL) {
			return;
		}
		this.authService.isLoggedStatus.subscribe(isLogged => isLogged ? this.start() : this.stop());
	}

	private start(): void {
		const user = this.authService.auth?.user;
		if (!user?.id || user.id === this.trackedUid) {
			return;
		}
		this.stop();
		if (!this.configService.isFeatureFlagEnabled('presenceFeatureFlag', [user.id])) {
			return;
		}

		this.trackedUid = user.id;
		this.lastInteractionAt = Date.now();
		this.context = this.readContext();

		this.zone.runOutsideAngular(() => {
			this.db ??= getDatabase(getApp(), environment.firebase.databaseURL);
			goOnline(this.db);
			this.connRef = push(ref(this.db, `presence/${this.authService.projectId}/${user.id}`));
			const name = displayName(user);

			// Fires on first connect and again after every reconnect — e.g. when iOS killed the
			// socket while Safari was in the background. The node may already have been removed by
			// onDisconnect, so it is rebuilt in full each time.
			this.teardown.push(onValue(ref(this.db, '.info/connected'), snap => {
				if (snap.val() === true) {
					this.register(name);
				}
			}));
			this.teardown.push(onValue(ref(this.db, '.info/serverTimeOffset'), snap => {
				this.serverTimeOffset = snap.val() ?? 0;
			}));

			const onActivity = () => this.markActive();
			const onMedia = (event: Event) => {
				if (event.target instanceof HTMLMediaElement && !event.target.paused) {
					this.markActive();
				}
			};
			const onVisibilityChange = () => {
				if (document.visibilityState === 'hidden') {
					// Best effort: Safari may freeze the page before this is sent. The stale heartbeat
					// and onDisconnect cover it when it isn't.
					this.write(update(this.connRef, { visible: false }));
				} else {
					this.lastInteractionAt = Date.now();
					this.register(name);
				}
			};
			const onPageShow = (event: PageTransitionEvent) => {
				if (event.persisted) {
					this.lastInteractionAt = Date.now();
					this.register(name);
				}
			};

			ACTIVITY_EVENTS.forEach(type => document.addEventListener(type, onActivity, { capture: true, passive: true }));
			MEDIA_EVENTS.forEach(type => document.addEventListener(type, onMedia, { capture: true, passive: true }));
			document.addEventListener('visibilitychange', onVisibilityChange);
			window.addEventListener('pageshow', onPageShow);

			const heartbeat = setInterval(() => this.write(update(this.connRef, { heartbeat: serverTimestamp() })), HEARTBEAT_INTERVAL_MS);

			this.teardown.push(() => {
				ACTIVITY_EVENTS.forEach(type => document.removeEventListener(type, onActivity, { capture: true }));
				MEDIA_EVENTS.forEach(type => document.removeEventListener(type, onMedia, { capture: true }));
				document.removeEventListener('visibilitychange', onVisibilityChange);
				window.removeEventListener('pageshow', onPageShow);
				clearInterval(heartbeat);
			});
		});

		const navigation = this.router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
			.subscribe(() => this.onNavigation());
		this.teardown.push(() => navigation.unsubscribe());
	}

	private stop(): void {
		this.teardown.forEach(dispose => dispose());
		this.teardown = [];
		if (this.connRef) {
			this.write(remove(this.connRef));
		}
		if (this.db) {
			// Closing the socket also makes the server run the onDisconnect removal, so the node is
			// cleared even if the remove above loses the race with sign-out.
			goOffline(this.db);
		}
		this.connRef = null;
		this.trackedUid = null;
		this.since = null;
	}

	private async register(name: string): Promise<void> {
		const connRef = this.connRef;
		if (!connRef) {
			return;
		}
		try {
			await onDisconnect(connRef).remove();
			await set(connRef, {
				since: this.since ?? serverTimestamp(),
				heartbeat: serverTimestamp(),
				lastActive: this.lastActiveValue(),
				visible: document.visibilityState === 'visible',
				device: this.device,
				name,
				...this.context,
			});
			this.lastActiveWriteAt = Date.now();
			if (this.since === null && this.connRef === connRef) {
				this.since = (await get(child(connRef, 'since'))).val();
			}
		} catch (error) {
			console.warn('Presence: failed to register connection', error);
		}
	}

	private markActive(): void {
		const now = Date.now();
		this.lastInteractionAt = now;
		if (!this.connRef || document.visibilityState !== 'visible' || now - this.lastActiveWriteAt < ACTIVE_WRITE_THROTTLE_MS) {
			return;
		}
		this.lastActiveWriteAt = now;
		this.write(update(this.connRef, { lastActive: serverTimestamp(), visible: true }));
	}

	private onNavigation(): void {
		this.context = this.readContext();
		this.lastInteractionAt = Date.now();
		if (!this.connRef) {
			return;
		}
		this.lastActiveWriteAt = this.lastInteractionAt;
		this.write(update(this.connRef, {
			...this.context,
			lastActive: serverTimestamp(),
			visible: document.visibilityState === 'visible',
		}));
	}

	/**
	 * A reconnect isn't an interaction. Keep the real last-interaction time (converted to server
	 * time) so a page left untouched doesn't flash back to active when its socket reconnects.
	 */
	private lastActiveValue(): number | object {
		const sinceInteraction = Date.now() - this.lastInteractionAt;
		return sinceInteraction < ACTIVE_WRITE_THROTTLE_MS ? serverTimestamp() : this.lastInteractionAt + this.serverTimeOffset;
	}

	private readContext(): PresenceContext {
		let categoryId: string | null = null;
		let courseId: string | null = null;
		let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
		while (route) {
			categoryId = route.paramMap.get('categoryId') ?? categoryId;
			courseId = route.paramMap.get('courseId') ?? courseId;
			route = route.firstChild;
		}
		const page = (this.router.url || location.pathname).split(/[?#]/)[0].slice(0, 500);
		return { page, categoryId, courseId };
	}

	private write(operation: Promise<unknown>): void {
		operation.catch(error => console.warn('Presence: write failed', error));
	}
}

function detectDevice(): PresenceDevice {
	const ua = navigator.userAgent;
	// iPadOS reports a desktop Mac user agent; touch support gives it away.
	if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
		return 'ios';
	}
	if (/Android/.test(ua)) {
		return 'android';
	}
	return 'desktop';
}

function displayName(user: User): string {
	const fullName = [user.name, user.lastName].filter(Boolean).join(' ');
	return (fullName || user.username || user.email || user.id).slice(0, 200);
}
